/**
 * @file UsersManagement.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "../../../../utils/useForm";
import SkinPage from "../../SkinPage";
import DgButton from "../../../../components/DgButton";
import Request from "../../../../utils/fetchUtils";
import DgModal from "../../../../components/DgModal";
import TextInput from "../../../../components/forms/TextInput";
import SelectInput from "../../../../components/forms/SelectInput";
import SearchInput from "../../../../components/forms/SearchInput";
import RolesEnum from "../../../../enums/roles.json";
import Swal from "sweetalert2";
import "./UsersManagement.css";
import Widget from "../../widgets/Widget";
import { getFromSession } from "../../../../utils/sessionUtils";
import Loader from "../../widgets/Loader";
import { IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const emptyUserSchema = {
  fullName: "",
  password: "",
  email: "",
  phone: "",
  role: "",
};

const UserManagement = () => {
  const sessionKey = getFromSession("SK");
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [allUsers, setAllUsers] = useState([]);
  const [singleData, setSingleData, resetSingleData, setAllSingleData] =
    useForm({ ...emptyUserSchema });
  const [userData, setUserData, resetData] = useForm({
    ...emptyUserSchema,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const roles = RolesEnum.filter((role) => role.value !== "customer");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const data = await Request("user");
    data.data.sort((a, b) => {
      const nameA = a.fullName?.toLowerCase() || "";
      const nameB = b.fullName?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    })

    const filteredUsers = data.data.filter(
      (user) => user.role !== "customer" && user.isActive
    );

    if (data) {
      setIsLoading(false);
      setUsers(filteredUsers);
      setAllUsers(filteredUsers);
    }
  }, []);

  const handleOpenEditModal = (data) => () => {
    setIsModalEditOpen(true);
    setAllSingleData(data);
  };
  const handleCreateUser = async () => {
    const { fullName, email, password, phone, role } = userData;
    if (
      fullName !== "" &&
      email !== "" &&
      password !== "" &&
      phone !== "" &&
      role !== ""
    ) {
      setIsLoading(true);
      const res = await Request("user", userData, "POST", {});
      setIsLoading(false);
      if (res.ok) {
        Swal.fire({
          title: "User successfully created",
          text: "",
          icon: "success",
        }).then(() => {
          fetchData();
          setIsModalOpen(false);
          resetData();
        });
      }
    } else {
      Swal.fire({
        title: "Error",
        text: "All fields are required",
        icon: "error",
      });
    }
  };
  const handleUpdateUser = async () => {
    setIsLoading(true);
    const res = await Request("user", { ...singleData }, "PUT", {});
    setIsLoading(false);
    if (res.ok) {
      Swal.fire({
        title: "User successfully updated",
        text: "",
        icon: "success",
      }).then(() => {
        fetchData();
        setIsModalEditOpen(false);
        resetSingleData();
      });
    }
  };

  const handleDeleteUser = (user) => async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This user will be deleted and unrecoverable",
      icon: "warning",
      showCancelButton: true,
    }).then(async ({ isConfirmed }) => {
      if (isConfirmed) {
        const userKeyToDelete = user.SK;
        if (sessionKey !== userKeyToDelete) {
          setIsLoading(true);
          const res = await Request(`user/${user.userId}`, {}, "DELETE", {
            Userkey: user.SK, // DO NOT REMOVE THIS OR ADMIN WILL BE ERASED
          });
          setIsLoading(false);
          if (res.ok) {
            Swal.fire("Success", "User deleted", "success").then(() => {
              fetchData();
            });
          } else {
            Swal.fire("Error", res.message, "error");
          }
        } else {
          Swal.fire("Stop!", "You are trying to delete yourself", "error");
        }
      }
    });
  };

  const renderDate = (createdAt) => {
    const createdAtDate = new Date(createdAt);
    return createdAtDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const filterData = (role = filter, search = searchTerm) => {
    let filtered = allUsers;

    if (role !== "all") {
      filtered = filtered.filter((user) => user.role === role);
    }

    if (search.trim() !== "") {
      const lowerSearch = search.trim().toLowerCase();
      filtered = filtered.filter((user) =>
        user.fullName?.toLowerCase().includes(lowerSearch)
      );
    }

    setUsers(filtered);
  };

  const handleRoleChange = (e) => {
    const role = e?.target?.value || "all";
    setFilter(role);
    filterData(role, searchTerm);
  };
  const handleSearchChange = (e) => {
    const search = e?.target?.value || "";
    setSearchTerm(search);
    filterData(filter, search);
  };

  const handleResetFilters = () => {
    setFilter("all");
    setSearchTerm("");
    setUsers(allUsers);
  };
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <SkinPage>
      {isLoading && <Loader />}
      <Widget name={"users-management"} width={"full-width"}>
        <div className="toppers">
          {
            <div
              className="new-client"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              <img className="icon" src="/assets/icons/plus.svg" alt="" />
              <p className="new-client-button">New user</p>
            </div>
          }
          <div className="filters">
            <div>
              <div className="filter-title form-field">
                <SelectInput
                  customInput={{
                    label: "Filter by role",
                    name: "role",
                    action: handleRoleChange,
                    value: filter,
                  }}
                  data={[{ text: "All", value: "all" }, ...roles]}
                />
              </div>
            </div>
            <div>
            </div>            <div>
              <div className="filter-title form-field">
                <SearchInput
                  customInput={{
                    label: "Search by name",
                    name: "search",
                    value: searchTerm,
                    action: handleSearchChange,
                    tooltipBool: true,
                    tooltipText: "Search for a user by name",
                    placeholder: "Type a name...",
                  }}
                />
              </div>
            </div>
            <div>              <button 
                onClick={handleResetFilters}
                className="reset-filters-btn"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
        <table className="users-management-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Date Registered</th>
              <th>Account Type</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr>
                <td className="name">
                  <div className="user">
                    <div>
                      <span className="name">{u?.fullName}</span>
                      <span className="activity"></span>
                    </div>
                  </div>
                </td>
                <td className="email">
                  <div className="email">
                    <div>
                      <span className="name">{u?.email}</span>
                    </div>
                  </div>
                </td>
                <td>{renderDate(u.createdAt)}</td>
                <td>
                  {
                    (
                      roles.find((role) => role.value === u.role) || {
                        text: "-",
                      }
                    ).text
                  }
                </td>
                <td>
                  <Tooltip title="Edit User" placement="top-start">
                    <IconButton onClick={handleOpenEditModal(u)}>
                      <EditIcon style={{ color: "white" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete User" placement="top-start">
                    <IconButton onClick={handleDeleteUser(u)}>
                      <DeleteForeverIcon style={{ color: "white" }} />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <DgModal
          modalState={isModalOpen}
          handleModalState={() => {
            setIsModalOpen(!isModalOpen);
          }}
        >
          <h1 className="title">New User</h1>
          <div className="strategy-content">
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Full name",
                  name: "fullName",
                  tooltipBool: false,
                  value: userData.fullName,
                  action: (e) => {
                    setUserData(e);
                  },
                }}
              />
            </div>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Email",
                  name: "email",
                  tooltipBool: false,
                  value: userData.email,
                  type: "email",
                  action: (e) => {
                    setUserData(e);
                  },
                }}
              ></TextInput>
            </div>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Password",
                  name: "password",
                  tooltipBool: false,
                  value: userData.password,
                  type: "password",
                  action: (e) => {
                    setUserData(e);
                  },
                }}
              ></TextInput>
            </div>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Phone",
                  name: "phone",
                  tooltipBool: false,
                  value: userData.phone,
                  action: (e) => {
                    setUserData(e);
                  },
                }}
              ></TextInput>
            </div>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Role",
                  name: "role",
                  value: userData.role,
                  required: true,
                  action: (e) => {
                    setUserData(e);
                  },
                }}
                data={[{ value: "", text: "Choose a role" }, ...roles]}
              />
            </div>
          </div>
          <DgButton
            category="primary"
            size="large"
            text="Create user"
            action={handleCreateUser}
          ></DgButton>
        </DgModal>
        <DgModal
          modalState={isModalEditOpen}
          handleModalState={() => {
            setIsModalEditOpen(!isModalEditOpen);
          }}
        >
          <h1 className="title">Update User</h1>
          <div className="strategy-content">
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Full name",
                  name: "fullName",
                  tooltipBool: false,
                  value: singleData.fullName,
                  action: (e) => {
                    setSingleData(e);
                  },
                }}
              />
            </div>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Email",
                  name: "email",
                  tooltipBool: false,
                  value: singleData.email,
                  type: "email",
                  readOnly: true,
                  action: (e) => {
                    setSingleData(e);
                  },
                }}
              ></TextInput>
            </div>
            {/* <div className="form-field">
            <TextInput
              customInput={{
                label: "Password",
                name: "password",
                tooltipBool: false,
                value: singleData.password,
                type: "password",
                action: (e) => {
                  setSingleData(e);
                },
              }}
            ></TextInput>
          </div> */}
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Phone",
                  name: "phone",
                  tooltipBool: false,
                  value: singleData.phone,
                  action: (e) => {
                    setSingleData(e);
                  },
                }}
              />
            </div>
            {/* <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Role",
                  name: "role",
                  value: singleData.role,
                  action: (e) => {
                    setSingleData(e);
                  },
                }}
                data={[{ value: "", text: "Choose a role" }, ...roles]}
              />
            </div> */}
          </div>
          <DgButton
            category="primary"
            size="large"
            text="Update User"
            action={handleUpdateUser}
          ></DgButton>
        </DgModal>
      </Widget>
    </SkinPage>
  );
};

export default UserManagement;
